import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { SEO_ROUTE_CONFIG } from '../../Setup/Config/seo-config';

/**
 * SEOHead - Reusable SEO component for public pages
 * Automatically detects current route and applies appropriate meta tags
 * 
 * @param {string} title - Custom title (optional, overrides config)
 * @param {string} description - Custom description (optional, overrides config)
 * @param {string} keywords - Custom keywords (optional, overrides config)
 * @param {string} ogImage - Custom OG image (optional, overrides config)
 * @param {string} canonical - Custom canonical URL (optional, overrides config)
 * @param {string} type - OG type (default: 'website')
 */
export const SEOHead = ({
    title,
    description,
    keywords,
    ogImage,
    canonical,
    type = 'website'
}) => {
    const location = useLocation();
    const currentPath = location.pathname;

    // Get config for current route or use default (backward compatibility)
    const routeConfig = SEO_ROUTE_CONFIG[currentPath] || SEO_ROUTE_CONFIG.default;

    // Get base URL from environment variable
    const siteUrl = process.env.REACT_APP_FE_URL;

    // Override with props if provided, else use config, else use default fallback
    const finalTitle = title || routeConfig?.title || SEO_ROUTE_CONFIG.default.title;
    const finalDescription = description || routeConfig?.description || SEO_ROUTE_CONFIG.default.description;
    const finalKeywords = keywords || routeConfig?.keywords || SEO_ROUTE_CONFIG.default.keywords;
    const finalOgImage = ogImage || routeConfig?.ogImage || SEO_ROUTE_CONFIG.default.ogImage;
    const finalCanonical = canonical || routeConfig?.canonical || `${siteUrl}${currentPath}`;
    const finalOgType = type || routeConfig?.ogType || 'website';
    const finalOgLocale = routeConfig?.ogLocale || 'en_AE';

    // Full OG image URL - ensure it's absolute (handle SSR/react-snap)
    // react-snap runs in Node.js, so window is undefined during pre-rendering
    const getBaseUrl = () => {
        if (siteUrl) return siteUrl;
        // During react-snap pre-rendering, window is undefined
        // Use environment variable or fallback to empty string (will be absolute in config)
        if (typeof window !== 'undefined') return window.location.origin;
        return '';
    };
    const ogImageUrl = finalOgImage.startsWith('http')
        ? finalOgImage
        : `${getBaseUrl()}${finalOgImage}`;

    // Ensure canonical URL is absolute (works with react-snap)
    const absoluteCanonical = finalCanonical.startsWith('http')
        ? finalCanonical
        : `${getBaseUrl()}${finalCanonical}`;

    return (
        <Helmet>
            {/* Basic Meta Tags */}
            <title>{finalTitle}</title>
            <meta name="description" content={finalDescription} />
            <meta name="keywords" content={finalKeywords} />
            <link rel="canonical" href={absoluteCanonical} />

            {/* Enhanced Robots Meta */}
            <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
            <meta name="author" content="DGTIA Team" />
            <meta name="language" content="en" />
            <meta name="revisit-after" content="7 days" />

            {/* Open Graph Tags (Facebook, LinkedIn) */}
            <meta property="og:title" content={finalTitle} />
            <meta property="og:description" content={finalDescription} />
            <meta property="og:image" content={ogImageUrl} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:type" content="image/png" />
            <meta property="og:image:alt" content="DGTIA Tools Logo" />
            <meta property="og:url" content={absoluteCanonical} />
            <meta property="og:type" content={finalOgType} />
            <meta property="og:site_name" content="DGTIA Tools" />
            <meta property="og:locale" content={finalOgLocale} />

            {/* Twitter Card Tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content="@dgtiaofficial" />
            <meta name="twitter:creator" content="@dgtiaofficial" />
            <meta name="twitter:title" content={finalTitle} />
            <meta name="twitter:description" content={finalDescription} />
            <meta name="twitter:image" content={ogImageUrl} />
            <meta name="twitter:image:alt" content="DGTIA Tools Logo" />
        </Helmet>
    );
};

